import React, { useRef } from "react";
import { AttachmentIcon } from "vega-icons";

import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { useToast } from "@vega-core/components/Toast/useToast";
import {
  MAX_EMAIL_ATTACHMENTS,
  MAX_FILE_SIZE,
} from "@vega-core/constants/global";
import { m } from "@vega-core/paraglide/messages";
import { formatFileSize } from "@vega-core/utils/formatNumber";

type EmailAttachmentButtonProps = {
  accept: string;
  disabled?: boolean;
  multiple?: boolean;
  maxFileSize?: number;
  maxFiles?: number;
  onFileSelect: (files: File[]) => void;
};

export const EmailAttachmentButton = ({
  accept,
  disabled = false,
  multiple = true,
  maxFileSize = MAX_FILE_SIZE,
  maxFiles = MAX_EMAIL_ATTACHMENTS,
  onFileSelect,
}: EmailAttachmentButtonProps) => {
  const showToast = useToast();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);

    if (files.length > 0) {
      if (files.length > maxFiles) {
        showToast({
          title: m.common_invalidFileTitle(),
          description: m.errors_tooManyFilesSelected({ maxFiles }),
          variant: "destructive",
        });

        return;
      }

      const hasOversizedFiles = files.some((file) => file.size > maxFileSize);

      if (hasOversizedFiles) {
        const maxSizeFormatted = formatFileSize(maxFileSize);
        showToast({
          title: m.common_invalidFileTitle(),
          description: m.errors_fileSizeTooLarge_plural({
            maxSizeMB: maxSizeFormatted,
          }),
          variant: "destructive",
        });

        return;
      }

      onFileSelect(files);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  return (
    <>
      <input
        accept={accept}
        className="hidden"
        data-testid="email-attachment-input"
        multiple={multiple}
        onChange={handleFileChange}
        ref={fileInputRef}
        type="file"
      />
      <ThemedButton
        dataTestId="email-attachment-button"
        disabled={disabled}
        icon={<AttachmentIcon className="size-4" />}
        onClick={() => fileInputRef.current?.click()}
        type="button"
        variant="secondary"
      >
        {m.common_addAttachments()}
      </ThemedButton>
    </>
  );
};
